"use client"

import { cn } from "@/lib/utils"
import { Table2, TrendingUp, TrendingDown, Minus } from "lucide-react"

export interface DataColumn {
  key: string
  label: string
  align?: "left" | "center" | "right"
}

export interface DataRow {
  [key: string]: string | number | { value: string; trend?: "up" | "down" | "flat" }
}

function CellValue({ cell }: { cell: string | number | { value: string; trend?: "up" | "down" | "flat" } }) {
  if (typeof cell === "object" && cell !== null && "value" in cell) {
    const TrendIcon = cell.trend === "up" ? TrendingUp : cell.trend === "down" ? TrendingDown : Minus
    const trendColor = cell.trend === "up" ? "text-emerald-600 dark:text-emerald-400" :
                        cell.trend === "down" ? "text-red-500 dark:text-red-400" :
                        "text-muted-foreground"
    return (
      <span className={cn("inline-flex items-center gap-1 font-medium", trendColor)}>
        {cell.value}
        <TrendIcon className="h-3 w-3" />
      </span>
    )
  }
  return <>{cell}</>
}

export function DataTableCard({
  title,
  columns,
  rows,
  className,
}: {
  title?: string
  columns: DataColumn[]
  rows: DataRow[]
  className?: string
}) {
  return (
    <div className={cn(
      "rounded-xl border border-border/60 bg-card/60 backdrop-blur-sm overflow-hidden",
      className,
    )}>
      {title && (
        <div className="px-4 py-2.5 border-b border-border/40 bg-secondary/20 flex items-center gap-2">
          <Table2 className="h-4 w-4 text-primary" />
          <span className="text-xs font-semibold text-foreground">{title}</span>
          <span className="text-micro text-muted-foreground ml-auto">
            {rows.length} 条数据
          </span>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border/30">
              {columns.map(col => (
                <th
                  key={col.key}
                  className={cn(
                    "px-3 py-2 text-caption font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap",
                    col.align === "right" ? "text-right" :
                    col.align === "center" ? "text-center" : "text-left"
                  )}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr
                key={i}
                className="border-b border-border/20 last:border-0 hover:bg-secondary/20 transition-colors animate-in fade-in slide-in-from-left-1"
                style={{ animationDelay: `${i * 40}ms`, animationFillMode: "both" }}
              >
                {columns.map(col => (
                  <td
                    key={col.key}
                    className={cn(
                      "px-3 py-2 text-sm text-foreground whitespace-nowrap",
                      col.align === "right" ? "text-right" :
                      col.align === "center" ? "text-center" : "text-left"
                    )}
                  >
                    <CellValue cell={row[col.key]} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
